import React from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Package } from "lucide-react"

import { ProductResponseDto } from "@/api/api-client"
import { apiClient } from "@/api/client"
import { EmptyState } from "@/components/ui/empty-state"
import { LoadingState } from "@/components/ui/loading-state"
import { useLanguage } from "@/hooks/useLanguage"
import { useVendorProducts } from "@/hooks/useProducts"
import { toastSuccess } from "@/lib/toast"

import { BatchManagementSheet } from "./BatchManagementSheet"
import { EditProductSheet } from "./EditProductSheet"
import { InventoryFilterBar } from "./inventory/InventoryFilterBar"
import { InventoryProductCard } from "./inventory/InventoryProductCard"
import { InventoryProductRow } from "./inventory/InventoryProductRow"
import { useInventoryFilters } from "./inventory/useInventoryFilters"

interface VendorInventoryProps {
  focusedProductId?: number | null
}

const VendorInventory = ({ focusedProductId }: VendorInventoryProps) => {
  const queryClient = useQueryClient()
  const { t, language } = useLanguage()
  const text = t.vendorInventory
  const [editingProduct, setEditingProduct] = React.useState<ProductResponseDto | null>(null)
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const [isBatchOpen, setIsBatchOpen] = React.useState(false)
  const [batchProduct, setBatchProduct] = React.useState<ProductResponseDto | null>(null)
  const [highlightedId, setHighlightedId] = React.useState<number | null>(null)

  const { data: products, isLoading } = useVendorProducts()
  const { filteredProducts, statusFilter, setStatusFilter, viewMode, setViewMode } =
    useInventoryFilters({ products })

  React.useEffect(() => {
    if (!focusedProductId) return
    setHighlightedId(focusedProductId)
    const scrollTimer = setTimeout(() => {
      document
        .getElementById(`inventory-product-${focusedProductId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" })
    }, 100)
    const clearTimer = setTimeout(() => setHighlightedId(null), 2000)
    return () => {
      clearTimeout(scrollTimer)
      clearTimeout(clearTimer)
    }
  }, [focusedProductId])

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiClient.deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor-products"] })
      toastSuccess(text.deleteSuccess)
    },
  })

  const handleEdit = (product: ProductResponseDto) => {
    setEditingProduct(product)
    setIsDialogOpen(true)
  }

  const handleManageBatches = (product: ProductResponseDto) => {
    setBatchProduct(product)
    setIsBatchOpen(true)
  }

  if (isLoading) {
    return (
      <LoadingState
        label={text.loading}
        spinnerClassName="rounded-none"
        labelClassName="text-foreground opacity-20"
      />
    )
  }

  return (
    <div className="space-y-6">
      <InventoryFilterBar
        labels={text}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {filteredProducts.length === 0 ? (
        <EmptyState icon={Package} message={text.empty} />
      ) : viewMode === "comfortable" ? (
        <div className="space-y-32">
          {filteredProducts.map((product) => (
            <InventoryProductCard
              key={product.id}
              product={product}
              labels={text}
              isHighlighted={highlightedId === product.id}
              isDeleting={deleteMutation.isPending}
              onManageBatches={handleManageBatches}
              onEdit={handleEdit}
              onDelete={deleteMutation.mutate}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-2 border-t-2 border-foreground pt-4">
          {filteredProducts.map((product) => (
            <InventoryProductRow
              key={product.id}
              product={product}
              labels={text}
              isHighlighted={highlightedId === product.id}
              isDeleting={deleteMutation.isPending}
              onManageBatches={handleManageBatches}
              onEdit={handleEdit}
              onDelete={deleteMutation.mutate}
            />
          ))}
        </div>
      )}

      <EditProductSheet
        product={editingProduct}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        language={language}
      />

      <BatchManagementSheet
        product={batchProduct}
        open={isBatchOpen}
        onOpenChange={setIsBatchOpen}
        language={language}
      />
    </div>
  )
}

export default VendorInventory
